<script setup>
import { ref, getCurrentInstance } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useLoading } from '@/composables/useLoading'

const { proxy } = getCurrentInstance()
const $t = proxy.$t
const { showLoading, hideLoading } = useLoading()

const form = useForm({
    description: '',
    urgent: false,
    photo: null,
})

const photoPreview = ref(null)
const photoInput = ref(null)

const submit = () => {
    if (photoInput.value?.files[0]) {
        form.photo = photoInput.value.files[0]
    }

    showLoading($t('Submitting bug report...'));
    const routeName = proxy.route ? proxy.route('client.bugs.store') : '/bugs'
    form.post(routeName, {
        preserveScroll: true,
        onSuccess: () => {
            clearPhotoFileInput()
            form.reset()
            photoPreview.value = null
        },
        onFinish: () => hideLoading(),
    })
}

const selectNewPhoto = () => {
    photoInput.value?.click()
}

const updatePhotoPreview = () => {
    const photo = photoInput.value?.files[0]

    if (!photo) return

    const reader = new FileReader()

    reader.onload = (e) => {
        photoPreview.value = e.target.result
    }

    reader.readAsDataURL(photo)
}

const clearPhotoFileInput = () => {
    if (photoInput.value?.value) {
        photoInput.value.value = null
    }
}
</script>

<template>
    <form @submit.prevent="submit" class="space-y-6">
        <!-- Description -->
        <div class="field">
            <label for="description" class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('What is going on?') }}
            </label>
            <Textarea
                id="description"
                v-model="form.description"
                rows="5"
                class="w-full"
                :class="{ 'p-invalid': form.errors.description }"
                :placeholder="$t('Please describe the issue or suggestion in detail...')"
            />
            <small v-if="form.errors.description" class="p-error">
                {{ form.errors.description }}
            </small>
        </div>

        <!-- Photo Upload -->
        <div class="field">
            <label class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('Photo') }} <small class="text-surface-500">({{ $t('Optional') }})</small>
            </label>
            
            <div class="border-2 border-dashed border-surface-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                <input
                    ref="photoInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="updatePhotoPreview"
                >

                <!-- Photo Preview -->
                <div v-if="photoPreview" class="mb-4">
                    <img
                        :src="photoPreview"
                        alt="Preview"
                        class="max-h-48 mx-auto rounded-lg object-contain"
                    >
                    <Button
                        type="button"
                        :label="$t('Change Photo')"
                        icon="pi pi-camera"
                        severity="secondary"
                        size="small"
                        class="mt-2"
                        @click="selectNewPhoto"
                    />
                </div>

                <!-- Upload Area -->
                <div v-else @click="selectNewPhoto" class="cursor-pointer">
                    <i class="pi pi-cloud-upload text-4xl text-surface-400 mb-4"></i>
                    <div class="text-surface-600">
                        <p class="text-lg font-medium">{{ $t('Upload a file') }}</p>
                        <p class="text-sm">PNG, JPG, GIF {{ $t('up to') }} 1MB</p>
                    </div>
                </div>
            </div>

            <small v-if="form.errors.photo" class="p-error">
                {{ form.errors.photo }}
            </small>
        </div>

        <!-- Urgent Checkbox -->
        <div class="field">
            <div class="flex items-center gap-3">
                <div class="flex items-center gap-3 p-3 border border-orange-200 rounded-lg bg-orange-50 flex-1">
                    <Checkbox
                        id="urgent"
                        v-model="form.urgent"
                        :binary="true"
                    />
                    <label for="urgent" class="text-sm font-medium text-orange-700 cursor-pointer flex items-center gap-2">
                        <i class="pi pi-exclamation-triangle text-orange-600"></i>
                        {{ $t('Mark this box if this is a very urgent problem') }}
                    </label>
                </div>
                
                <!-- Actions -->
                <Button
                    type="submit"
                    :label="form.processing ? $t('Sending...') : $t('Send')"
                    icon="pi pi-send"
                    :loading="form.processing"
                    :disabled="form.processing"
                />
            </div>
            <small v-if="form.errors.urgent" class="p-error">
                {{ form.errors.urgent }}
            </small>
        </div>

        <div v-if="form.recentlySuccessful" class="flex items-center gap-2 text-green-600 pt-4">
            <i class="pi pi-check-circle"></i>
            <span class="text-sm font-medium">{{ $t('Submitted.') }}</span>
        </div>
    </form>
</template>

<style scoped>
.field {
    display: flex;
    flex-direction: column;
}

.p-error {
    color: rgb(239 68 68);
    font-size: 0.75rem;
    margin-top: 0.25rem;
}

.p-invalid {
    border-color: rgb(239 68 68);
}
</style>